import { imageDimensions } from "../shared/image-dimensions";
import {
  MAX_SCREENSHOT_BYTES,
  MAX_SCREENSHOT_EDGE,
  pixelCrop,
  type Crop,
  type ReportScreenshot,
} from "../shared/report-screenshots";

export async function loadScreenshot(file: Blob): Promise<HTMLCanvasElement> {
  if (file.size > 10 * 1024 * 1024)
    throw new Error("Choose a screenshot smaller than 10 MB.");
  const size = await imageDimensions(file);
  if (size.w * size.h > 24_000_000)
    throw new Error("Choose a screenshot smaller than 24 megapixels.");
  const bitmap = await createImageBitmap(file);
  try {
    const scale = Math.min(1, 3840 / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const ctx = canvas.getContext("2d")!;
    ctx.fillStyle = "white";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return canvas;
  } finally {
    bitmap.close();
  }
}
export async function encodeScreenshot(
  source: HTMLCanvasElement,
  crop: Crop,
): Promise<ReportScreenshot> {
  const rect = pixelCrop(crop, source.width, source.height);
  const scale = Math.min(
    1,
    MAX_SCREENSHOT_EDGE / Math.max(rect.width, rect.height),
  );
  const canvas = document.createElement("canvas");
  try {
    for (let attempt = 0; attempt < 4; attempt++) {
      canvas.width = Math.max(
        1,
        Math.round(rect.width * scale * 0.8 ** attempt),
      );
      canvas.height = Math.max(
        1,
        Math.round(rect.height * scale * 0.8 ** attempt),
      );
      canvas
        .getContext("2d")!
        .drawImage(
          source,
          rect.x,
          rect.y,
          rect.width,
          rect.height,
          0,
          0,
          canvas.width,
          canvas.height,
        );
      for (const quality of [0.9, 0.75, 0.6]) {
        const blob = await new Promise<Blob>((resolve, reject) =>
          canvas.toBlob(
            (b) =>
              b
                ? resolve(b)
                : reject(new Error("Could not prepare screenshot.")),
            "image/jpeg",
            quality,
          ),
        );
        if (blob.size <= MAX_SCREENSHOT_BYTES) {
          const dataUrl = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(String(reader.result));
            reader.onerror = () =>
              reject(new Error("Could not read screenshot."));
            reader.readAsDataURL(blob);
          });
          return { dataUrl, width: canvas.width, height: canvas.height };
        }
      }
    }
    throw new Error("Screenshot is too detailed. Select a smaller area.");
  } finally {
    canvas.width = canvas.height = 0;
  }
}
// Capture exactly one frame, never audio; stop every track even on errors.
export async function captureScreen(): Promise<HTMLCanvasElement> {
  const stream = await navigator.mediaDevices.getDisplayMedia({
    video: { width: { ideal: 3840 }, height: { ideal: 2160 } },
    audio: false,
  });
  const video = document.createElement("video");
  video.muted = true;
  video.srcObject = stream;
  let timeout: ReturnType<typeof setTimeout> | undefined;
  try {
    await Promise.race([
      video.play(),
      new Promise<never>((_, reject) => {
        timeout = setTimeout(
          () =>
            reject(
              new Error(
                "Screen capture timed out. Try uploading a screenshot.",
              ),
            ),
          10_000,
        );
      }),
    ]);
    if (!video.videoWidth || !video.videoHeight)
      throw new Error("No screen image available.");
    const scale = Math.min(
      1,
      3840 / Math.max(video.videoWidth, video.videoHeight),
    );
    const canvas = document.createElement("canvas");
    canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
    canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
    canvas
      .getContext("2d")!
      .drawImage(video, 0, 0, canvas.width, canvas.height);
    return canvas;
  } finally {
    if (timeout) clearTimeout(timeout);
    stream.getTracks().forEach((t) => t.stop());
    video.pause();
    video.srcObject = null;
  }
}
